import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Lazy load proposal v2 pages
const Dashboard = React.lazy(() => import("./Dashboard"));
const ProposalsList = React.lazy(() => import("./ProposalsList"));
const NewProposal = React.lazy(() => import("./NewProposal"));
const ProposalDetail = React.lazy(() => import("./ProposalDetail"));
const EditProposal = React.lazy(() => import("./EditProposal"));
const PublicProposalSign = React.lazy(() => import("./PublicProposalSign"));

/**
 * Proposals v2 Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (p/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/p/permissions.ts
 */
const pModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: "p",
  name: "Proposals v2",
  description: "Create, send, track and sign business proposals with opportunity linking and invoice generation",
  version: "1.0.0",
  enabled: true,
  category: "core",
  order: 4,
  icon: "FiFileText",
  color: "#6B46C1",

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      path: "/p",
      component: Dashboard,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/p/list",
      component: ProposalsList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/p/new",
      component: NewProposal,
      permissionLevel: "create",
      useLayout: true,
    },
    {
      path: "/p/new/:opportunityId",
      component: NewProposal,
      permissionLevel: "create",
      useLayout: true,
    },
    {
      path: "/p/sign/:slug",
      component: PublicProposalSign,
      public: true, // Public route - no auth required
      permissions: [],
    },
    {
      path: "/p/:id",
      component: ProposalDetail,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/p/:id/edit",
      component: EditProposal,
      permissionLevel: "edit",
      useLayout: true,
    },
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime
  navigation: [
    {
      label: "Dashboard",
      path: "/p",
      icon: "📊",
      permissionLevel: "view",
    },
    {
      label: "All Proposals",
      path: "/p/list",
      icon: "📋",
      permissionLevel: "view",
    },
    {
      label: "New Proposal",
      path: "/p/new",
      icon: "➕",
      permissionLevel: "create",
    },
  ],

  // Permissions come from the backend (single source of truth).
  // See: business-builder-backend/src/modules/p/permissions.ts
  // These empty arrays get replaced at runtime by ModulePermissionsContext.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: "New Proposal",
      path: "/p/new",
      icon: "➕",
      description: "Create a new business proposal",
    },
    {
      label: "View All",
      path: "/p/list",
      icon: "📋",
      description: "Browse all proposals",
    },
  ],

  // CHANGELOG
  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2026-02-10",
      title: "Initial Release - Proposals v2",
      features: [
        "Full lifecycle tracking: Draft -> Sent -> Viewed -> Signed -> Delivered",
        "Opportunity linking with payment schedule pull-through",
        "Invoice generation from payment schedule items",
        "Digital signature capture with email verification",
        "Activity timeline and delivery checklist",
        "Admin notifications on view/sign events",
        "Public signing page for clients",
      ],
      notes: "Complete rewrite of the proposals system with opportunity integration and enhanced tracking.",
    },
  ],

  // ROADMAP
  roadmap: [
    {
      title: "Partnership Proposals",
      description: "Co-author partnership deals with reduced rates and royalties",
      targetVersion: "1.1.0",
      priority: "medium",
      status: "planned",
      features: [
        "Partnership proposal type with custom terms",
        "Royalty tracking and reporting",
        "Co-author module credit system",
      ],
    },
  ],
};

export default pModuleConfig;
